import "./Releases.css";
import { forwardRef, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { FaPlay, FaPause } from "react-icons/fa";
import { SONGS } from "../../../../data/songs";
import { ARTIST_NAME } from "../../../../data/artist";
import usePreviewPlayer from "../../../../utils/usePreviewPlayer";
import { playableTracks, releaseMeta } from "../../../../utils/releaseTracks";
import Copyright from "../../../../components/Copyright/Copyright";
import { track as trackEvent } from "../../../../utils/analytics";
import {
  isHidden,
  isOut,
  outLabel,
  isTeaser,
  stageOf,
  TEASER_PATH,
  currentTeaserKey,
} from "../../../../utils/releaseStatus";
import SocialLinks from "../../../../components/SocialLinks/SocialLinks";

const STAGE_ORDER = { teaser: 0, announced: 1, released: 2 };

// Every release by its stage (utils/releaseStatus.js): teasers first, then
// announced ones, then the rest newest first. Hidden ones aren't listed. "Latest" goes to the newest one that's out; announced ones
// show "Out <date>" instead. A teaser shows its blurred `teaserArtwork`.
// Only one COMING SOON teaser at a time (the first one in songs.js)
const TEASER_KEY = currentTeaserKey(SONGS);
const ITEMS = Object.entries(SONGS)
  .filter(([slug, song]) => !isHidden(song) && (!isTeaser(song) || slug === TEASER_KEY))
  .map(([slug, song]) => ({
    slug,
    ...song,
    upcoming: isTeaser(song),
    cover: isTeaser(song) ? song.teaserArtwork : song.artwork,
  }))
  .sort(
    (a, b) =>
      STAGE_ORDER[stageOf(a)] - STAGE_ORDER[stageOf(b)] ||
      (b.releaseDate || "").localeCompare(a.releaseDate || "")
  );
const RELEASES = ITEMS.filter((item) => !item.upcoming);

// Fetched quietly once the home page has finished loading, so pressing play
// or opening a release feels instant: the preview clips and the EP songs'
// own artwork (the covers themselves are already on the page). Skipped when
// the visitor asked to save data.
const WARM_UP = [
  ...new Set(
    ITEMS.flatMap((release) =>
      release.upcoming
        ? []
        : [
            ...playableTracks(release).map((track) => track.preview.src),
            ...(release.tracks || []).map((track) => track.artwork).filter(Boolean),
          ]
    )
  ),
];
let warmedUp = false;
function warmUp() {
  if (warmedUp || navigator.connection?.saveData) return;
  warmedUp = true;
  const start = () => {
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
    idle(() =>
      WARM_UP.forEach((url) => fetch(url, { priority: "low" }).catch(() => {}))
    );
  };
  if (document.readyState === "complete") start();
  else window.addEventListener("load", start, { once: true });
}

// The light on the cover follows a real mouse only (not touch)
const canFollow = () => window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;

// Covers already revealed this visit. When coming back to the page they show
// right away, so the cover can glide back into place (and nothing re-animates).
const revealed = new Set();

function ReleaseItem({ release, index, latest, player, onActive }) {
  const { slug, cover, upcoming } = release;
  const title = upcoming ? "Coming soon" : release.title;
  // A single's preview, or each song's preview of an EP/album in turn (none for a teaser)
  const tracks = upcoming ? [] : playableTracks(release);
  const trackRef = useRef(0); // which song of the release is loaded
  const alreadyRevealed = revealed.has(slug);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
    skip: alreadyRevealed,
  });
  if (inView) revealed.add(slug);
  const visible = alreadyRevealed || inView;

  const playing = player.playingId === slug;
  const progress = player.currentId === slug ? player.progress : 0;

  // Play song `i`; when it ends, roll on to the next one (EPs/albums)
  const playTrack = (i) => {
    trackRef.current = i;
    const next = i + 1 < tracks.length ? () => playTrack(i + 1) : () => (trackRef.current = 0);
    player.play(tracks[i].preview, slug, { onEnd: next });
  };
  // Resume the loaded song, or start from the first one
  const play = () => {
    playTrack(player.currentId === slug ? trackRef.current : 0);
    trackEvent("preview_play", { song: slug });
  };

  // A soft light follows the mouse across the cover (CSS variables --sx/--sy
  // on the cover); the cover itself stays still
  const coverRef = useRef(null);
  const followLight = (event) => {
    const cover = coverRef.current;
    if (!cover || !canFollow()) return;
    const box = cover.getBoundingClientRect();
    cover.classList.add("is-lit");
    cover.style.setProperty("--sx", `${(((event.clientX - box.left) / box.width) * 100).toFixed(1)}%`);
    cover.style.setProperty("--sy", `${(((event.clientY - box.top) / box.height) * 100).toFixed(1)}%`);
  };
  const dimLight = () => coverRef.current?.classList.remove("is-lit");
  const linkProps = {
    // A teaser opens at /soon, so its real address (its title) isn't shown
    to: `/${upcoming ? TEASER_PATH : slug}`,
    unstable_viewTransition: true,
    state: { fromHome: true, morph: true },
  };

  return (
    <article
      ref={ref}
      className={`release ${upcoming ? "is-upcoming" : ""} ${visible ? "is-visible" : ""} ${
        alreadyRevealed ? "no-reveal" : ""
      } ${playing ? "is-playing" : ""}`}
      // Covers in the same row (up to 5) fade in one after another; the delay
      // restarts for each row, so covers further down don't wait longer
      style={{ "--reveal-delay": `${(index % 5) * 80}ms` }}
      onMouseEnter={() => onActive(slug)}
      onMouseLeave={() => onActive(null)}
      onFocus={() => onActive(slug)}
      onBlur={() => onActive(null)}
    >
      <div
        className="release-cover"
        ref={coverRef}
        onMouseMove={followLight}
        onMouseLeave={dimLight}
      >
        <Link
          {...linkProps}
          className="release-cover-link"
          aria-label={`${title}, open`}
          data-cursor={upcoming ? "Soon" : "Open"}
        >
          <img
            src={cover}
            // A teaser's cover is already obscured in the file itself
            alt={upcoming ? "New release, coming soon" : `${title} cover`}
            style={{ viewTransitionName: `cover-${slug}` }}
          />
        </Link>

        {latest ? (
          <span className="release-badge release-badge--latest">Latest</span>
        ) : null}
        {stageOf(release) === "announced" ? (
          <span className="release-badge">{outLabel(release)}</span>
        ) : null}

        {tracks.length ? (
          <>
            <button
              className="release-play"
              // The cursor hides here and the button itself grows instead
              data-cursor-hide
              data-magnetic
              onClick={playing ? player.pause : play}
              aria-label={playing ? `Pause ${title} preview` : `Play ${title} preview`}
            >
              {playing ? <FaPause /> : <FaPlay className="release-play-icon" />}
            </button>
            <div className="release-progress" aria-hidden="true">
              <div style={{ transform: `scaleX(${progress})` }} />
            </div>
          </>
        ) : null}
      </div>

      <Link {...linkProps} className="release-title">
        {title}
      </Link>
      {upcoming ? (
        release.year ? <p className="release-meta">Arriving {release.year}</p> : null
      ) : (
        <p className="release-meta">{releaseMeta(release)}</p>
      )}
    </article>
  );
}

/*
 * Home page releases: a "shelf" of covers over a blurred, darkened view of the
 * gallery behind it. Hovering a release (or playing its preview) fades in a
 * blur of its cover colors, which glows with the music while it plays.
 */
const Releases = forwardRef(function Releases(_, ref) {
  const sectionRef = useRef(null);
  const player = usePreviewPlayer({ levelTargetRef: sectionRef });
  const [hovered, setHovered] = useState(null);
  // Cover colors only while hovering or playing; otherwise the gallery shows through
  const active = hovered || player.playingId;
  useEffect(warmUp, []);

  const setRefs = (node) => {
    sectionRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  };

  return (
    <section className="releases" ref={setRefs} aria-labelledby="releases-heading">
      {/* During page transitions (Safari/Firefox can't blur then): a blurred copy of the photo
          standing in for the see-through blur (see viewTransitionFlag.js) */}
      <div className="releases-blur-standin" aria-hidden="true" />
      <div className="releases-backdrop" aria-hidden="true">
        {ITEMS.map(({ slug, cover }) => (
          <div
            key={slug}
            className={`releases-backdrop-layer ${active === slug ? "is-active" : ""}`}
            style={{ backgroundImage: `url(${cover})` }}
          />
        ))}
        <div className="releases-backdrop-shade" />
      </div>

      <div className="releases-inner">
        <header className="releases-header">
          <h2 id="releases-heading">Releases</h2>
          <span className="releases-count">
            {String(RELEASES.filter(isOut).length).padStart(2, "0")}
          </span>
        </header>

        <div className="releases-grid">
          {ITEMS.map((release, index) => (
            <ReleaseItem
              key={release.slug}
              release={release}
              index={index}
              latest={release === RELEASES.find(isOut)}
              player={player}
              onActive={setHovered}
            />
          ))}
        </div>

        {/* Copyright line at the very bottom of the page (the year updates itself).
            Phones: the follow icons sit above it, as on the song pages (on
            desktop they float in the corner instead; see Music.js) */}
        <footer>
          <SocialLinks
            white
            music
            position="centered"
            label={`follow ${ARTIST_NAME}`}
            className="releases-follow"
            exclude={["soundcloud"]} // SoundCloud only on the Contact panel
          />
          <Copyright className="releases-footer" />
        </footer>
      </div>
    </section>
  );
});

export default Releases;
